import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronsUpDown,
  Columns2,
  Headset,
  LayoutGrid,
  LogIn,
  LogOut,
  Map,
  PlusCircle,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import {
  getUser,
  isAdminUser,
  isAuthConfigured,
  login as authLogin,
  logout as authLogout,
  REGISTER_URL,
} from "../../libs/auth";
import { useAuthVersion } from "@/hooks/use-auth-version";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
//@ts-ignore
import logoUrl from "../../img/vietmap.png";

type NavItem = {
  title: string
  url: string
  icon: React.ElementType
  /** Các route con cũng làm sáng mục này (vd /detail/:id thuộc Maps). */
  match: (path: string) => boolean
};

const SUPPORT_URL = "https://zalo.me/3189066936017422854";
const DOCS_URL = "https://maps.vietmap.vn/docs/";

export function AppSidebar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { setOpenMobile } = useSidebar();
  // Roles live in the token — re-render when it arrives.
  useAuthVersion();

  const navItems: NavItem[] = [
    {
      title: t("Maps"),
      url: "/manage",
      icon: Map,
      match: (p) => p === "/" || p === "/manage" || p.startsWith("/detail/"),
    },
    {
      title: t("So sánh"),
      url: "/compare",
      icon: Columns2,
      match: (p) => p === "/compare",
    },
    {
      title: t("Tạo mới"),
      url: "/create",
      icon: PlusCircle,
      match: (p) => p === "/create",
    },
  ];

  // Only for Console SystemAdmins / AccountManagers (the API re-checks).
  const adminItems: NavItem[] = isAdminUser()
    ? [{ title: t("Quản trị"), url: "/admin", icon: ShieldCheck, match: (p) => p === "/admin" }]
    : [];

  const renderItems = (items: NavItem[]) =>
    items.map((item) => (
      <SidebarMenuItem key={item.url}>
        <SidebarMenuButton asChild isActive={item.match(pathname)} tooltip={item.title}>
          <Link to={item.url} onClick={() => setOpenMobile(false)}>
            <item.icon />
            <span>{item.title}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    ));

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              className="hover:bg-transparent active:bg-transparent"
            >
              <Link to="/manage" onClick={() => setOpenMobile(false)}>
                <img
                  src={logoUrl}
                  alt="Vietmap"
                  className="size-8 shrink-0 rounded-full"
                />
                <div className="grid flex-1 text-start leading-tight">
                  <span className="truncate font-bold">Vietmap</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {t("Style editor")}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t("Quản lý")}</SidebarGroupLabel>
          <SidebarMenu>{renderItems(navItems)}</SidebarMenu>
        </SidebarGroup>
        {adminItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>{t("Quản trị")}</SidebarGroupLabel>
            <SidebarMenu>{renderItems(adminItems)}</SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip={t("Help & Supports")}>
              <a href={SUPPORT_URL} target="_blank" rel="noreferrer noopener">
                <Headset />
                <span>{t("Help & Supports")}</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip={t("Product & services")}>
              <a href={DOCS_URL} target="_blank" rel="noreferrer noopener">
                <LayoutGrid />
                <span>{t("Product & services")}</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

function NavUser() {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();
  useAuthVersion();
  const user = getUser();

  if (!user) {
    if (!isAuthConfigured()) return null;
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton tooltip={t("Đăng nhập")} onClick={() => authLogin()}>
            <LogIn />
            <span>{t("Đăng nhập")}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
        <SidebarMenuItem>
          <SidebarMenuButton tooltip={t("Đăng ký tài khoản Vietmap")} asChild>
            <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer" data-wd-key="sidebar:register">
              <UserPlus />
              <span>{t("Đăng ký")}</span>
            </a>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  const name = user.fullName || user.username || t("Tài khoản");
  const initials = (user.fullName || user.username || user.email || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarFallback className="bg-primary text-primary-foreground rounded-lg text-xs font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-start text-sm leading-tight">
                <span className="truncate font-semibold">{name}</span>
                {user.email && (
                  <span className="text-muted-foreground truncate text-xs">
                    {user.email}
                  </span>
                )}
              </div>
              <ChevronsUpDown className="ms-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="font-normal">
              <div className="grid text-start text-sm leading-tight">
                <span className="truncate font-semibold">{name}</span>
                {user.email && (
                  <span className="text-muted-foreground truncate text-xs">
                    {user.email}
                  </span>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onClick={() => authLogout()}>
              <LogOut />
              {t("Đăng xuất")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
